import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { ChartDatapoint } from '../../../../core/types/chart-data.js';
import type { ChartUnit } from '../../../../core/types/chart-unit.js';
import type { Formatter } from '../../../../core/types/formatter.js';
import type { SeriesActionsTemplate } from '../../../../core/types/series-actions-template.js';
import type { XYChartTooltipPayload } from '../../../../xy-chart/types/xy-chart.js';
import { FlameTooltip } from '../../../shared/components/tooltip/FlameTooltip.js';
import { SELF_VALUE_FIELD, VALUE_FIELD } from '../../../shared/constants.js';
import { buildFlameTooltipBaseData } from '../../../shared/utils/build-tooltip-base-data.js';
import { formatFlameValue } from '../../../shared/utils/format-flame-value.js';
import { FLAME_GRAPH_TOOLTIP_MESSAGES } from './messages.js';

interface DefaultTooltipProps<TData extends ChartDatapoint> {
  payload: XYChartTooltipPayload;
  data: readonly TData[];
  labelAccessor: string;
  selfValueAccessor?: string;
  formatter?: Formatter;
  unit?: ChartUnit;
  seriesActions?: (datapoint: TData) => SeriesActionsTemplate;
  rootValue: number;
  globalActions?: (datapoint: TData) => ReactNode[];
}

export function DefaultTooltip<TData extends ChartDatapoint>({
  payload,
  data,
  labelAccessor,
  selfValueAccessor,
  formatter,
  unit,
  seriesActions,
  rootValue,
  globalActions,
}: Readonly<DefaultTooltipProps<TData>>): ReactNode {
  const intl = useIntl();
  const {
    datapoint,
    originalDatapoint,
    color,
    label,
    subheader,
    actions,
    selfValue,
  } = buildFlameTooltipBaseData({
    payload,
    data,
    labelAccessor,
    selfValueAccessor,
    formatter,
    unit,
    seriesActions,
    intl,
  });

  const totalValue = Number(datapoint?.[VALUE_FIELD] ?? 0);

  const toRelativePct = (value: number): string | undefined => {
    if (rootValue <= 0) {
      return undefined;
    }
    return `${Math.round((value / rootValue) * 1000) / 10}%`;
  };

  const resolvedGlobalActions =
    globalActions !== undefined && originalDatapoint !== undefined
      ? globalActions(originalDatapoint)
      : undefined;

  return (
    <FlameTooltip
      label={label}
      subheader={subheader}
      color={color}
      valueLabel={intl.formatMessage(
        FLAME_GRAPH_TOOLTIP_MESSAGES.absolutePercentOfTotal,
      )}
      totalValue={formatFlameValue(totalValue, formatter, unit)}
      totalRelativeValue={toRelativePct(totalValue)}
      selfValue={selfValue}
      selfRelativeValue={
        selfValueAccessor !== undefined
          ? toRelativePct(Number(datapoint?.[SELF_VALUE_FIELD] ?? 0))
          : undefined
      }
      actions={actions}
      globalActions={resolvedGlobalActions}
    />
  );
}
